import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Chip,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Rating,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, People, Edit, Delete, Star, AdminPanelSettings, PersonOff } from '@mui/icons-material';
import api from '../../utils/api';
import { Agent } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../types';
import PageHeader from '../../components/Common/PageHeader';
import AddAgentDialog from '../../components/Agents/AddAgentDialog';
import EditAgentDialog from '../../components/Agents/EditAgentDialog';
import RateAgentDialog from '../../components/Agents/RateAgentDialog';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import ToastNotification from '../../utils/toastNotification';

const AgentsPage: React.FC = () => {
  const { user } = useAuth();
  
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [agentToRate, setAgentToRate] = useState<Agent | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [agentToPromote, setAgentToPromote] = useState<Agent | null>(null);
  const [targetRole, setTargetRole] = useState<Role>(Role.SUPER_ADMIN);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (agent: Agent) => {
    setSelectedAgent(agent);
    setEditDialogOpen(true);
  };

  const handleRateClick = (agent: Agent) => {
    setAgentToRate(agent);
    setRateDialogOpen(true);
  };

  const handleDeleteClick = (agent: Agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleRoleChangeClick = (agent: Agent, newRole: Role) => {
    setAgentToPromote(agent);
    setTargetRole(newRole);
    setRoleDialogOpen(true);
  };

  const handleRoleChangeConfirm = async () => {
    if (!agentToPromote) return;
    try {
      await api.patch(`/agents/${agentToPromote.id}/role`, { role: targetRole });
      await loadAgents();
      setRoleDialogOpen(false);
      setAgentToPromote(null);
      const label = targetRole === Role.SUPER_ADMIN ? 'Super Admin' : 'Admin';
      ToastNotification.success(`Agent role changed to ${label} successfully!`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update role';
      ToastNotification.error(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;

    try {
      await api.delete(`/agents/${agentToDelete.id}`);
      await loadAgents();
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      ToastNotification.success('Agent deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting agent:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete agent';
      ToastNotification.error(errorMessage);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'Agent',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 40, height: 40 }}>
            {params.row.user?.firstName?.[0]}{params.row.user?.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.user?.firstName} {params.row.user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.user?.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.user?.phone || 'N/A',
    },
    {
      field: 'commission',
      headerName: 'Commission',
      width: 120,
      renderCell: (params) => `${params.row.commission}%`,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Rating
            value={params.row.rating ?? 0}
            precision={0.5}
            readOnly
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {(params.row.rating ?? 0).toFixed(1)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'properties',
      headerName: 'Properties',
      width: 120,
      valueGetter: (params) => params.row._count?.properties || 0,
    },
    {
      field: 'clients',
      headerName: 'Clients',
      width: 100,
      valueGetter: (params) => params.row._count?.clients || 0,
    },
    {
      field: 'totalSales',
      headerName: 'Total Sales',
      width: 130,
      renderCell: (params) => `$${params.row.totalSales.toLocaleString()}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.user?.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.user?.isActive ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.user?.role === Role.SUPER_ADMIN ? 'Super Admin' : 'Admin'}
          size="small"
          color={params.row.user?.role === Role.SUPER_ADMIN ? 'secondary' : 'default'}
          variant={params.row.user?.role === Role.SUPER_ADMIN ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 190,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Rate Agent">
            <IconButton size="small" onClick={() => handleRateClick(params.row)} color="warning">
              <Star fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.user?.role === Role.ADMIN ? (
            <Tooltip title="Promote to Super Admin">
              <IconButton size="small" onClick={() => handleRoleChangeClick(params.row, Role.SUPER_ADMIN)} color="secondary">
                <AdminPanelSettings fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Demote to Admin">
              <IconButton size="small" onClick={() => handleRoleChangeClick(params.row, Role.ADMIN)} color="warning">
                <PersonOff fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(params.row)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (user?.role !== Role.SUPER_ADMIN) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
          px: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          ⛔ Access Denied
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          You don't have permission to view this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Agents Management"
        subtitle="Manage your property agents, track performance, and monitor sales"
        icon={<People sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Agents' },
        ]}
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={() => setAddDialogOpen(true)}
            sx={{
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': { boxShadow: 4 },
            }}
          >
            Add Agent
          </Button>
        }
      />

      <Paper
        sx={{
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <DataGrid
          rows={agents}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            minHeight: 500,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f8f9fa',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9f9f9' },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
          }}
        />
      </Paper>

      <AddAgentDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={loadAgents}
      />

      <EditAgentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAgent(null);
        }}
        onSuccess={loadAgents}
        agent={selectedAgent}
      />

      <RateAgentDialog
        open={rateDialogOpen}
        onClose={() => {
          setRateDialogOpen(false);
          setAgentToRate(null);
        }}
        onSuccess={loadAgents}
        agent={agentToRate}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Agent"
        message={`Are you sure you want to delete agent ${agentToDelete?.user?.firstName} ${agentToDelete?.user?.lastName}? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setAgentToDelete(null);
        }}
      />

      <ConfirmDialog
        open={roleDialogOpen}
        title={targetRole === Role.SUPER_ADMIN ? 'Promote to Super Admin' : 'Demote to Admin'}
        message={
          targetRole === Role.SUPER_ADMIN
            ? `Are you sure you want to promote ${agentToPromote?.user?.firstName} ${agentToPromote?.user?.lastName} to Super Admin? They will have full access to the system.`
            : `Are you sure you want to demote ${agentToPromote?.user?.firstName} ${agentToPromote?.user?.lastName} to Admin? They will lose Super Admin privileges.`
        }
        onConfirm={handleRoleChangeConfirm}
        onCancel={() => {
          setRoleDialogOpen(false);
          setAgentToPromote(null);
        }}
      />
    </Box>
  );
};

export default AgentsPage;
